import { ArrowRight } from 'lucide-react';
import optimisticNewsWebp from '../../client/static/optimistic-banner.webp';
import { articlesUrl } from '../../shared/common';
import { routes } from 'wasp/client/router';
import { useAuth } from 'wasp/client/auth';
import { LinkButton } from '../../components/ui';

export default function Hero() {
  const { data: user } = useAuth();
  const newsletterCta = user
    ? {
        to: routes.NewsletterRoute.to,
        label: user.sendNewsletter ? 'Manage newsletter' : 'Get the newsletter',
      }
    : {
        to: routes.SignupRoute.to,
        label: 'Get the newsletter',
      };

  return (
    <div className="relative pt-14 w-full">
      <TopGradient />
      <BottomGradient />
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-8xl px-6 lg:px-8">
          <div className="lg:mb-18 mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold text-neutral-900 sm:text-6xl dark:text-white">
              Discover the <span className="italic text-primary">Brighter</span>{' '}
              Side of News
            </h1>
            <p className="mt-6 mx-auto max-w-2xl text-lg leading-8 text-neutral-600 dark:text-white">
              Your daily dose of positive news, inspiring stories, and progress
              that proves the world is getting better, one headline at a time.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <LinkButton
                to={articlesUrl}
                className="group inline-flex items-center gap-2 rounded-xl bg-primary-600 px-6 py-3 text-[15px] font-semibold text-white shadow-sm shadow-primary/30 transition-all duration-200 hover:gap-3 hover:bg-primary-700 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Start reading
                <ArrowRight
                  size={16}
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </LinkButton>
              <LinkButton
                to={newsletterCta.to}
                className="inline-flex items-center gap-2 rounded-xl bg-white/90 px-6 py-3 text-[15px] font-medium text-primary ring-1 ring-inset ring-primary/40 shadow-xs transition-colors duration-200 hover:bg-primary/10 hover:ring-primary/70 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-neutral-900 dark:text-white dark:ring-neutral-500 dark:hover:bg-neutral-800 dark:hover:ring-neutral-300"
              >
                {newsletterCta.label}
              </LinkButton>
            </div>
          </div>
          <div className="mt-14 flow-root sm:mt-14">
            <div className="-m-2 flex justify-center rounded-xl lg:-m-4 lg:rounded-2xl lg:p-4">
              <img
                src={optimisticNewsWebp}
                alt="App screenshot"
                width={1000}
                height={530}
                loading="lazy"
                className="rounded-md shadow-2xl ring-1 ring-neutral-900/10"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopGradient() {
  return (
    <div
      className="absolute top-0 right-0 -z-10 transform-gpu overflow-hidden w-full blur-3xl sm:top-0"
      aria-hidden="true"
    >
      <div
        className="aspect-1020/880 w-220 flex-none sm:right-1/4 sm:translate-x-1/2 dark:hidden bg-linear-to-tr from-amber-400 to-secondary-300 opacity-40"
        style={{
          clipPath:
            'polygon(80% 20%, 90% 55%, 50% 100%, 70% 30%, 20% 50%, 50% 0)',
        }}
      />
    </div>
  );
}

function BottomGradient() {
  return (
    <div
      className="absolute inset-x-0 top-[calc(100%-40rem)] sm:top-[calc(100%-65rem)] -z-10 transform-gpu overflow-hidden blur-3xl"
      aria-hidden="true"
    >
      <div
        className="relative aspect-1020/880 sm:-left-3/4 sm:translate-x-1/4 dark:hidden bg-linear-to-br from-primary-500 to-fuchsia-200  opacity-50 w-288.75"
        style={{
          clipPath: 'ellipse(80% 30% at 80% 50%)',
        }}
      />
    </div>
  );
}
